/**
 * Image upload with a two-stage size guarantee.
 *
 * Stage 1 (here, in the browser): a canvas pass that shrinks the picture below
 * the limit before a single byte goes over the wire. Stage 2 (the Worker): the
 * Cloudflare Images binding re-checks and re-encodes, and the API refuses to put
 * anything over the limit into R2. Either stage alone would be enough to hold the
 * rule; both together mean a large original never costs an upload round trip.
 */
import { useState } from 'react'
import { api, reportClientError } from '../lib/api'
import { HEIC_ACCEPT, isHeic } from '../lib/heic'
import { base64ToBytes } from '../../shared/base64.js'
import { useCanBrowse, useAnonVersion } from '../lib/viewmode'
import { GatedImage } from './GatedImage'
import { ConfirmModal, Field, Modal, Notice, Spinner, useToast, fmtBytes, fmtKb } from './ui'
import { Icon } from './Icon'
import { Dropzone } from './Dropzone'

/**
 * Shrink with canvas until the encoded blob fits `maxBytes`.
 * Returns the original file untouched if it already fits.
 *
 * Exported for the folder import in Settings, which sends several hundred pictures
 * one after another: doing this in the browser is what keeps that from being several
 * hundred oversized uploads for the Worker to shrink and, in the worst case, refuse.
 */
export async function shrinkInBrowser(file, maxBytes) {
  // A HEIC is re-encoded whatever its size, when this browser can open one at all
  // (Safari): the copy that goes on is then one every browser can show.
  const heic = isHeic(file)
  if (file.size <= maxBytes && !heic) return { file, resized: false }
  if (!file.type.startsWith('image/') && !heic) return { file, resized: false }

  const bitmap = await createImageBitmap(file).catch(() => null)
  // Let the server deal with it — it converts HEIC the browser could not open.
  if (!bitmap) return { file, resized: false, undecodable: true }

  const attempts = [
    [1600, 0.82], [1280, 0.78], [1024, 0.72], [900, 0.66],
    [720, 0.6], [600, 0.55], [480, 0.5], [400, 0.42],
  ]

  for (const [maxWidth, quality] of attempts) {
    const scale = Math.min(1, maxWidth / bitmap.width)
    const w = Math.max(1, Math.round(bitmap.width * scale))
    const h = Math.max(1, Math.round(bitmap.height * scale))
    const canvas = document.createElement('canvas')
    canvas.width = w
    canvas.height = h
    const ctx = canvas.getContext('2d')
    ctx.imageSmoothingQuality = 'high'
    ctx.drawImage(bitmap, 0, 0, w, h)
    const blob = await new Promise((resolve) => canvas.toBlob(resolve, 'image/webp', quality))
    if (blob && blob.size <= maxBytes) {
      bitmap.close?.()
      return {
        file: new File([blob], file.name.replace(/\.[^.]+$/, '') + '.webp', { type: 'image/webp' }),
        resized: true,
        width: w,
        height: h,
      }
    }
  }
  bitmap.close?.()
  // Send the original and let the Worker try; it will reject rather than store an oversized file.
  return { file, resized: false, exhausted: true }
}

/** The shrunk file as base64, which is how a staged picture travels on the form. */
const toBase64 = (file) =>
  new Promise((resolve, reject) => {
    const r = new FileReader()
    r.onload = () => resolve(String(r.result).split(',')[1])
    r.onerror = () => reject(new Error(`${file.name} could not be read`))
    r.readAsDataURL(file)
  })

/**
 * A HEIC this browser cannot open, converted by the Worker instead.
 *
 * Only Safari decodes HEIC, so on any other browser the canvas pass above has nothing
 * to draw. `media/prepare` runs the upload's own conversion and hands the copy back
 * without storing it — the same picture the tray would have made in Safari.
 */
const convertOnServer = async (file) => {
  const fd = new FormData()
  fd.append('file', file)
  const res = await api('media/prepare', { method: 'POST', formData: fd })
  const ext = res.mime_type.split('/')[1] || 'webp'
  return new File([base64ToBytes(res.data)], `${file.name.replace(/\.[^.]+$/, '')}.${ext}`, {
    type: res.mime_type,
  })
}

const measure = async (file) => {
  const bitmap = await createImageBitmap(file).catch(() => null)
  if (!bitmap) return {}
  const { width, height } = bitmap
  bitmap.close?.()
  return { width, height }
}

const STAGE_ACCEPT = `image/jpeg,image/png,image/webp,image/gif,image/avif,${HEIC_ACCEPT}`

/**
 * Pictures for a record that does not exist yet.
 *
 * The uploader below posts the moment a file is chosen, which needs an id to attach
 * to — and a new update has none until its first save. So this one stops short of the
 * network: it shrinks each picture the same way and hands it back as base64 for the
 * form to stage, and the form uploads the tray once the record exists. That is the
 * path the file import already used; this puts the user's own pictures on it.
 *
 * Several at once, because an update usually arrives with more than one. The size
 * guarantee is unchanged — the canvas pass here, the Worker's re-check on upload.
 */
export function ImageStager({
  maxBytes = 307200,
  onStage,
  disabled,
  label = 'Add pictures to this update',
}) {
  const [busy, setBusy] = useState(false)
  const [status, setStatus] = useState(null)
  const [error, setError] = useState(null)

  const handle = async (fileList) => {
    const files = [...(fileList || [])]
    if (!files.length) return
    setError(null)
    setBusy(true)
    const staged = []
    const rejected = []
    try {
      for (const [i, file] of files.entries()) {
        if (!file.type.startsWith('image/') && !isHeic(file)) {
          rejected.push(`${file.name} is not an image`)
          continue
        }
        setStatus(
          files.length > 1
            ? `Preparing ${i + 1} of ${files.length} — ${file.name}…`
            : `Preparing ${file.name} (${fmtBytes(file.size)})…`
        )
        const prepared = await shrinkInBrowser(file, maxBytes)
        // The tray shows what it holds, and a HEIC this browser cannot open would sit
        // in it as a broken thumbnail — so the Worker converts it first.
        if (prepared.undecodable && isHeic(file)) {
          try {
            prepared.file = await convertOnServer(file)
          } catch (err) {
            rejected.push(`${file.name} could not be converted: ${err.message.replace(/\.$/, '')}`)
            continue
          }
        }
        // Said now rather than after the update is saved: the ordinary uploader can
        // hand an oversized file to the Worker and let it refuse, but a staged one
        // would not be refused until the tray uploads, long after this screen.
        if (prepared.file.size > maxBytes) {
          rejected.push(
            `${file.name} is ${fmtBytes(prepared.file.size)} and could not be shrunk under ` +
              `${fmtBytes(maxBytes)}`
          )
          continue
        }
        staged.push({
          data: await toBase64(prepared.file),
          mime_type: prepared.file.type,
          source_name: file.name,
          bytes: prepared.file.size,
          ...(await measure(prepared.file)),
        })
      }
      if (staged.length) onStage(staged)
      if (rejected.length) setError(rejected.join('. ') + '.')
    } catch (err) {
      setError(err.message)
    } finally {
      setBusy(false)
      setStatus(null)
    }
  }

  return (
    <div className="col">
      <Dropzone
        onFiles={handle}
        accept={STAGE_ACCEPT}
        multiple
        disabled={disabled}
        busy={busy}
        busyLabel={status ?? 'Working…'}
        icon="image"
        title={label}
      >
        Drop pictures or click to browse — several at once is fine. Anything larger
        than {Math.round(maxBytes / 1024)} KB is resized first.
      </Dropzone>

      {error && (
        <Notice tone="warn" icon="warning" title="Some pictures were not added">
          {error}
        </Notice>
      )}
    </div>
  )
}

export function ImageUpload({
  kind = 'other',
  partnerId,
  newsletterId,
  maxBytes = 307200,
  onUploaded,
  describe = true,
  label = 'Upload an image',
}) {
  const toast = useToast()
  const [busy, setBusy] = useState(false)
  const [status, setStatus] = useState(null)
  const [error, setError] = useState(null)
  // A stored image whose alt text could not be written: the upload succeeded, so
  // this is advice, not a failure.
  const [warning, setWarning] = useState(null)
  const [alt, setAlt] = useState('')
  const [wantDescribe, setWantDescribe] = useState(describe)

  const handle = async (file) => {
    if (!file) return
    setError(null)
    setWarning(null)
    setBusy(true)
    setStatus(`Preparing ${file.name} (${fmtBytes(file.size)})…`)
    try {
      const prepared = await shrinkInBrowser(file, maxBytes)
      if (prepared.resized) {
        setStatus(`Resized ${fmtBytes(file.size)} → ${fmtBytes(prepared.file.size)}, uploading…`)
      } else if (prepared.exhausted) {
        setStatus('Could not shrink it in the browser — asking the server to try…')
      } else {
        setStatus(`Uploading ${fmtBytes(file.size)}…`)
      }

      const fd = new FormData()
      fd.append('file', prepared.file)
      fd.append('kind', kind)
      if (partnerId) fd.append('partner_id', String(partnerId))
      if (newsletterId) fd.append('newsletter_id', String(newsletterId))
      if (alt.trim()) fd.append('alt_text', alt.trim())
      if (wantDescribe && !alt.trim()) fd.append('describe', '1')

      const res = await api('media', { method: 'POST', formData: fd })
      toast.ok(
        res.resized || prepared.resized
          ? `Uploaded — resized ${fmtBytes(res.original_bytes ?? file.size)} → ${fmtBytes(res.bytes)}`
          : `Uploaded (${fmtBytes(res.bytes)})`
      )
      // The image is stored either way, so a captioning failure is a warning on
      // the form rather than an error on the upload — but it is never silent.
      setWarning(res.alt_error ?? null)
      setStatus(null)
      setAlt('')
      onUploaded?.(res)
    } catch (err) {
      setError(err.message)
      setStatus(null)
      // An upload that never reached the Worker: too large for the edge, or a
      // connection that dropped part-way through the bytes.
      reportClientError('upload', err, {
        filename: file?.name,
        mime_type: file?.type,
        bytes: file?.size,
        entity: newsletterId ? 'newsletter' : partnerId ? 'partner' : 'media',
        entity_id: newsletterId ?? partnerId ?? null,
      })
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="col">
      <Dropzone
        onFiles={(files) => handle(files[0])}
        accept={STAGE_ACCEPT}
        busy={busy}
        busyLabel={status ?? 'Working…'}
        icon="image"
        title={label}
      >
        Drop a file or click to browse. Anything larger than{' '}
        {Math.round(maxBytes / 1024)} KB is resized before it is stored.
      </Dropzone>

      <Field
        label="Alt text"
        hint={
          /* The code is said here because it appears without being typed, and text that
             changes on its own between the box and the page reads as a bug. Only on an
             update: a partner photograph is already filed under its unit by partner_id,
             which the library filters and displays by. */
          [
            wantDescribe
              ? 'Leave empty and a vision model will write it for you.'
              : 'Describes the image for screen readers.',
            newsletterId
              ? 'The S4CA code of this update is added to the front when it saves, so the picture can be found by code.'
              : null,
          ]
            .filter(Boolean)
            .join(' ')
        }
      >
        <input
          className="input"
          value={alt}
          onChange={(e) => setAlt(e.target.value)}
          placeholder={wantDescribe ? 'Generated by AI if left empty' : 'Describe the image'}
          disabled={busy}
        />
      </Field>

      <label className="checkbox">
        <input
          type="checkbox"
          checked={wantDescribe}
          onChange={(e) => setWantDescribe(e.target.checked)}
          disabled={busy}
        />
        <span className="small">Let AI write the alt text when I leave it empty</span>
      </label>

      {error && (
        <Notice tone="danger" icon="warning" title="Upload rejected">
          {error}
        </Notice>
      )}

      {warning && (
        <Notice tone="warn" icon="ai" title="Stored, but with no alt text">
          {warning} You can type the alt text on the image below instead.
        </Notice>
      )}
    </div>
  )
}

/**
 * An image from the archive.
 *
 * A `GatedImage` that knows about media rows: it builds the URL and, for a view-only
 * account, asks for the anonymised copy. Everything about *when* the picture is fetched
 * — near the viewport, a few at a time — is in that component, and so is the reason.
 *
 * For a view-only account the Worker has already served a 24px copy of it — the face is
 * not in the file at all (see ANON in the Worker's media route). The class added here
 * only asks the browser to draw those pixels as squares instead of smoothing them, so
 * the picture reads as one deliberately obscured rather than as a smear. It is not
 * where the anonymising happens: take the CSS off and you have a thumbnail, not a
 * person.
 */
export const MediaImage = ({ id, alt, className = 'media-thumb', style, eager = false }) => {
  const canBrowse = useCanBrowse()
  const anonVersion = useAnonVersion()
  // The version rides in the URL for the accounts that get anonymised images. The
  // Worker ignores it — it always serves the current recipe — but the browser does not:
  // these are cached for an hour against the URL, so without it a changed recipe would
  // reach only the pictures this browser had never opened.
  const src = id ? (canBrowse ? `/api/media/${id}/file` : `/api/media/${id}/file?v=${anonVersion}`) : null
  return (
    <GatedImage
      src={src}
      alt={alt}
      className={`${className}${canBrowse ? '' : ' media-anon'}`}
      style={style}
      eager={eager}
    />
  )
}

/**
 * A picture staged on a form — base64, no record behind it yet — in the shape the
 * lightbox reads. Stored images already arrive as media rows and need no conversion.
 */
export const stagedPicture = (pic, label) => ({
  src: `data:${pic.mime_type};base64,${pic.data}`,
  alt_text: label,
  filename: pic.source_name || label,
  bytes: pic.bytes,
  width: pic.width,
  height: pic.height,
  mime_type: pic.mime_type,
})

/**
 * Full-size viewer for one image. Pass a media row as `image`, or anything
 * `stagedPicture` returns for one that is not stored yet; null closes it. Escape
 * and the dialog's ✕ both dismiss, courtesy of Modal.
 */
export function MediaLightbox({ image, onClose }) {
  return (
    <Modal
      open={!!image}
      onClose={onClose}
      size="xl"
      title={image?.alt_text || image?.filename || 'Image'}
      footer={
        <>
          <button className="btn" onClick={onClose}>
            Close
          </button>
          <span className="spacer" />
          <span className="small subtle">
            {fmtKb(image?.bytes)}
            {image?.width ? ` · ${image.width}${image.height ? ` × ${image.height}` : 'px wide'}` : ''}
            {image?.mime_type ? ` · ${image.mime_type}` : ''}
          </span>
        </>
      }
    >
      {image &&
        (image.src ? (
          <img className="media-full" src={image.src} alt={image.alt_text || ''} />
        ) : (
          /* Eager, and the one place in the app that is. A picture somebody has just
             clicked is not a thumbnail waiting its turn — and `.media-full` is sized by
             the image rather than by CSS, so before it loads there is no box for an
             observer to watch. */
          <MediaImage id={image.id} alt={image.alt_text} className="media-full" eager />
        ))}
    </Modal>
  )
}

/**
 * Ask the vision model for alt text for an image that is already stored.
 *
 * Shared by the media library and the newsletter editor so both offer the same
 * thing: a proposal dropped into the field, which the person then reads, edits
 * and saves like any other alt text. Nothing is written to the record here.
 */
export function AiAltTextButton({ mediaId, onWritten, disabled, size = 'btn-sm' }) {
  const toast = useToast()
  const [busy, setBusy] = useState(false)

  const run = async () => {
    setBusy(true)
    try {
      const res = await api(`media/${mediaId}/describe`, { method: 'POST', body: {} })
      onWritten(res.alt_text)
      toast.ok('Alt text written — review it, then save')
    } catch (err) {
      toast.error(err)
    } finally {
      setBusy(false)
    }
  }

  return (
    <button
      className={`btn ${size}`}
      onClick={run}
      disabled={busy || disabled}
      title="Ask a vision model to describe this image"
    >
      {busy ? <Spinner /> : <><Icon name="ai" size={14} /> Write with AI</>}
    </button>
  )
}

/**
 * One stored image with everything you can do to it in place: open it full size,
 * correct its alt text, or take it off the record.
 *
 * Editing alt text here rather than only at upload time is deliberate — the
 * upload fires the moment a file is chosen, so anything typed into the uploader's
 * alt field afterwards never reached the server.
 */
function AttachedImage({ image, canEdit, canDelete, canUseAi, onOpen, onSaved, onRemove }) {
  const toast = useToast()
  const [alt, setAlt] = useState(image.alt_text ?? '')
  const [busy, setBusy] = useState(false)
  const dirty = alt !== (image.alt_text ?? '')

  const save = async () => {
    setBusy(true)
    try {
      await api(`media/${image.id}`, { method: 'PUT', body: { alt_text: alt } })
      // The server trims before storing, so match it here or the field stays
      // "dirty" against a value that is already saved.
      setAlt(alt.trim())
      toast.ok('Alt text saved')
      onSaved?.()
    } catch (err) {
      toast.error(err)
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="media-item">
      <button
        type="button"
        className="media-open"
        onClick={() => onOpen(image)}
        title="Open full size"
      >
        <MediaImage id={image.id} alt={image.alt_text} />
      </button>

      {canEdit ? (
        <div className="media-item-edit">
          <input
            className="input input-sm"
            value={alt}
            onChange={(e) => setAlt(e.target.value)}
            placeholder="Alt text — the S4CA code is kept at the front for you"
            aria-label={`Alt text for ${image.filename || 'this image'}`}
            disabled={busy}
          />
          <div className="row row-tight">
            <button className="btn btn-sm btn-primary" onClick={save} disabled={!dirty || busy}>
              {busy ? <Spinner /> : 'Save'}
            </button>
            {canUseAi && <AiAltTextButton mediaId={image.id} onWritten={setAlt} disabled={busy} />}
            <span className="spacer" />
            {/*
              Plain rather than btn-danger: a gallery of red buttons reads as a
              warning about the pictures. The confirmation dialog is where the
              weight belongs.
            */}
            {canDelete && (
              <button className="btn btn-sm" onClick={() => onRemove(image)} disabled={busy}>
                Remove
              </button>
            )}
          </div>
        </div>
      ) : (
        <div className="media-meta subtle truncate">{image.alt_text || image.filename}</div>
      )}
    </div>
  )
}

/**
 * The gallery of images hanging off one record. Owns the lightbox and the
 * delete confirmation so every caller gets the same behaviour.
 */
export function AttachedImages({ images, user, onChanged, empty }) {
  const toast = useToast()
  const [viewing, setViewing] = useState(null)
  const [confirmRemove, setConfirmRemove] = useState(null)
  const [busy, setBusy] = useState(false)

  const remove = async () => {
    setBusy(true)
    try {
      await api(`media/${confirmRemove.id}`, { method: 'DELETE' })
      toast.ok('Image removed')
      setConfirmRemove(null)
      setViewing(null)
      onChanged?.()
    } catch (err) {
      toast.error(err)
    } finally {
      setBusy(false)
    }
  }

  if (!images?.length) return empty ?? <p className="small subtle">No images attached yet.</p>

  return (
    <>
      <div className={`media-grid${user.caps.update ? ' media-grid-edit' : ''}`}>
        {images.map((img) => (
          <AttachedImage
            key={img.id}
            image={img}
            canEdit={user.caps.update}
            canDelete={user.caps.delete}
            canUseAi={user.caps.useAi}
            onOpen={setViewing}
            onSaved={onChanged}
            onRemove={setConfirmRemove}
          />
        ))}
      </div>

      <MediaLightbox image={viewing} onClose={() => setViewing(null)} />

      <ConfirmModal
        open={!!confirmRemove}
        onClose={() => setConfirmRemove(null)}
        onConfirm={remove}
        busy={busy}
        title="Remove this image?"
        body={
          <p>
            <strong>{confirmRemove?.filename || confirmRemove?.alt_text || 'The image'}</strong> is
            deleted from storage and detached from this record. This cannot be undone, though the
            removal is recorded in the history.
          </p>
        }
      />
    </>
  )
}
